import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiDollarSign,
  FiEdit2,
  FiFileText,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiTrash2,
  FiXCircle,
} from 'react-icons/fi';
import useAPI from '../../hooks/useAPI';
import api from '../../Services/api';
import useDeleteDetailPengeluaran from '../../hooks/useDeleteDetail_pengeluaran';
import useDeleteJenisBeban from '../../hooks/useDeleteJenis_beban';
import useDeletePengeluaran from '../../hooks/useDeletePengeluaran';
import usePostDetailPengeluaran from '../../hooks/usePostDetail_pengeluaran';
import usePostJenisBeban from '../../hooks/usePostJenis_beban';
import usePostPengeluaran from '../../hooks/usePostPengeluaran';
import usePutDetailPengeluaran from '../../hooks/useUpdateDetail_pengeluaran';
import usePutJenisBeban from '../../hooks/useUpdateJenis_beban';
import usePutPengeluaran from '../../hooks/useUpdatePengeluaran';
import {
  calculateDistributedByCategory,
  calculateHakAmilForSource,
  isSuccessfulDonation,
  resolveDonationAmount,
  resolveTransactionFundingType,
} from '../../Services/donationBalance';
import endpointsAdmin from '../../Services/endpointAdmin';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  findFieldDeep,
  getAdminLembagaScope,
  matchItemToAdminScope,
  normalizeScopeValue,
} from './adminScope';
import { resolveAdminRole } from './roleAdmin';

const SOURCE_DANA_OPTIONS = [
  { value: 'dana_amil', label: 'Dana Amil' },
  { value: 'zakat', label: 'Zakat' },
  { value: 'infak', label: 'Infak' },
  { value: 'wakaf', label: 'Wakaf' },
];

const initialPengeluaranForm = {
  id_pengeluaran: '',
  id_detail_pengeluaran: '',
  id_jenis_beban: '',
  nominal: '',
  keterangan: '',
  id_user: '',
  id_detail_himpun: '',
  id_himpun: '',
  sumber_dana: '',
  user_input: '',
  lembaga: '',
  tanggal: new Date().toISOString().slice(0, 10),
  departemen: 'KL Bogor',
};

const initialJenisForm = {
  id_jenis_beban: '',
  nama_beban: '',
};

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function resolveId(payload, keys) {
  const sources = [
    payload,
    payload?.data,
    payload?.result,
    payload?.detail_pengeluaran,
    payload?.pengeluaran,
    Array.isArray(payload?.data) ? payload.data[0] : null,
    Array.isArray(payload?.result) ? payload.result[0] : null,
  ].filter(Boolean);

  for (const source of sources) {
    for (const key of keys) {
      if (source?.[key]) return source[key];
    }
  }

  return '';
}

function resolvePengeluaranId(item) {
  return item?.id_pengeluaran || item?.id || '';
}

function resolveDetailPengeluaranId(item) {
  return (
    item?.id_detail_pengeluaran ||
    item?.detail_pengeluaran?.id_detail_pengeluaran ||
    item?.detail_pengeluaran?.id ||
    item?.detailPengeluaran?.id_detail_pengeluaran ||
    item?.detailPengeluaran?.id ||
    item?.detail?.id_detail_pengeluaran ||
    item?.detail?.id ||
    ''
  );
}

function resolveJenisDonasiName(item, jenisDonasiList = []) {
  const kodeJenis =
    item?.kode_jenis_donasi ||
    item?.kodeJenisDonasi ||
    item?.jenis_donasi?.kode_jenis_donasi ||
    item?.jenisDonasi?.kode_jenis_donasi ||
    item?.jenis_donasi?.kode ||
    item?.jenisDonasi?.kode ||
    item?.kode_jenis ||
    '';

  const matchedJenis = jenisDonasiList.find((jenis) => {
    const itemKode =
      jenis?.kode_jenis_donasi ||
      jenis?.kodeJenisDonasi ||
      jenis?.kode ||
      jenis?.kode_jenis ||
      '';

    return normalizeScopeValue(itemKode) === normalizeScopeValue(kodeJenis);
  });

  return (
    item?.jenis_donasi?.nama_donasi ||
    item?.jenisDonasi?.nama_donasi ||
    matchedJenis?.nama_donasi ||
    matchedJenis?.namaDonasi ||
    item?.jenis_donasi?.nama ||
    item?.jenisDonasi?.nama ||
    matchedJenis?.nama ||
    matchedJenis?.nama_jenis ||
    item?.nama_jenis_donasi ||
    ''
  );
}

function resolveSourceDanaLabel(value) {
  const normalizedValue = normalizeSourceDanaValue(value);
  return SOURCE_DANA_OPTIONS.find((option) => option.value === normalizedValue)?.label || value || '-';
}

function normalizeSourceDanaValue(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

  if (normalized.includes('amil')) return 'dana_amil';
  if (normalized.includes('zakat')) return 'zakat';
  if (normalized.includes('infak') || normalized.includes('infaq') || normalized.includes('sedekah')) return 'infak';
  if (normalized.includes('wakaf')) return 'wakaf';

  return normalized;
}


function PengeluaranAdmin({ admin }) {
  const { data: pengeluaranData, loading, error, refetch: refetchPengeluaran } = useAPI(endpointsAdmin.pengeluaran.getAll);
  const { data: detailPengeluaranData, refetch: refetchDetailPengeluaran } = useAPI(endpointsAdmin.detail_pengeluaran.getAll);
  const { data: jenisBebanData, refetch: refetchJenisBeban } = useAPI(endpointsAdmin.jenis_beban.getAll);
  const { data: detailHimpunData } = useAPI(endpointsAdmin.detail_himpun.getAll);
  const { data: himpunData } = useAPI(endpointsAdmin.himpun.getAll);
  const { data: transaksiData } = useAPI(endpointsAdmin.transaksi.getAll);
  const { data: jenisDonasiData } = useAPI(endpointsAdmin.jenis_donasi.getAll);
  const { data: detailDonasiData } = useAPI(endpointsAdmin.detail_donasi.getAll);
  const { data: penyaluranData } = useAPI(endpointsAdmin.penyaluran_program.getAll);
  const { data: programData } = useAPI(endpointsAdmin.program.getAll);

  const { postPengeluaran, loading: creatingPengeluaran, error: createPengeluaranError } = usePostPengeluaran();
  const { putPengeluaran, loading: updatingPengeluaran, error: updatePengeluaranError } = usePutPengeluaran();
  const { deletePengeluaran, loading: deletingPengeluaran, error: deletePengeluaranError } = useDeletePengeluaran();
  const { postDetailPengeluaran, loading: creatingDetail, error: createDetailError } = usePostDetailPengeluaran();
  const { putDetailPengeluaran, loading: updatingDetail, error: updateDetailError } = usePutDetailPengeluaran();
  const { deleteDetailPengeluaran } = useDeleteDetailPengeluaran();
  const { postJenisBeban, loading: creatingJenis, error: createJenisError } = usePostJenisBeban();
  const { putJenisBeban, loading: updatingJenis, error: updateJenisError } = usePutJenisBeban();
  const { deleteJenisBeban, loading: deletingJenis, error: deleteJenisError } = useDeleteJenisBeban();

  const [pengeluaranForm, setPengeluaranForm] = useState(initialPengeluaranForm);
  const [jenisForm, setJenisForm] = useState(initialJenisForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);

  const lembagaScope = getAdminLembagaScope(admin);
  const adminId = String(findFieldDeep(admin, ['id_user', 'idUser', 'id']) || '').trim();
  const adminName = String(findFieldDeep(admin, ['nama_user', 'nama', 'name', 'username']) || 'Admin').trim();
  const currentRole = useMemo(() => resolveAdminRole(admin), [admin]);
  const canDeletePengeluaran = currentRole === 'super_admin' || currentRole === 'pimpinan';

  useEffect(() => {
    setPengeluaranForm((prev) => ({
      ...prev,
      id_user: prev.id_user || adminId,
      user_input: prev.user_input || adminName,
      lembaga: prev.lembaga || lembagaScope.name,
    }));
  }, [adminId, adminName, lembagaScope.name]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (!cancelled) {
          setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
        }
      } catch (fetchError) {
        if (!cancelled) {
          setAdminMitraRecords([]);
        }
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  const pengeluaranList = useMemo(() => extractArrayPayload(pengeluaranData, ['pengeluaran']), [pengeluaranData]);
  const detailPengeluaranList = useMemo(
    () => extractArrayPayload(detailPengeluaranData, ['detail_pengeluaran']),
    [detailPengeluaranData]
  );
  const jenisBebanList = useMemo(() => extractArrayPayload(jenisBebanData, ['jenis_beban']), [jenisBebanData]);
  const detailHimpunList = useMemo(() => extractArrayPayload(detailHimpunData, ['detail_himpun']), [detailHimpunData]);
  const himpunList = useMemo(() => extractArrayPayload(himpunData, ['himpun']), [himpunData]);
  const transaksiList = useMemo(() => extractArrayPayload(transaksiData, ['transaksi']), [transaksiData]);
  const jenisDonasiList = useMemo(() => extractArrayPayload(jenisDonasiData, ['jenis_donasi']), [jenisDonasiData]);
  const detailDonasiList = useMemo(() => extractArrayPayload(detailDonasiData, ['detail_donasi']), [detailDonasiData]);
  const penyaluranList = useMemo(() => extractArrayPayload(penyaluranData, ['penyaluran_program']), [penyaluranData]);
  const programList = useMemo(() => extractArrayPayload(programData, ['program']), [programData]);
  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  const scopedTransaksiList = useMemo(() => (
    transaksiList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [transaksiList, lembagaScope, adminMitraScope]);

  const scopedPenyaluranList = useMemo(() => (
    penyaluranList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [penyaluranList, lembagaScope, adminMitraScope]);

  const scopedPengeluaranList = useMemo(() => (
    pengeluaranList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [pengeluaranList, lembagaScope, adminMitraScope]);

  const detailPengeluaranMap = useMemo(
    () => new Map(detailPengeluaranList.map((item) => [String(item.id_detail_pengeluaran || item.id || ''), item])),
    [detailPengeluaranList]
  );

  const detailPengeluaranByPengeluaranMap = useMemo(
    () => new Map(detailPengeluaranList
      .filter((item) => item.id_pengeluaran || item.idPengeluaran || item.pengeluaran_id)
      .map((item) => [String(item.id_pengeluaran || item.idPengeluaran || item.pengeluaran_id), item])
    ),
    [detailPengeluaranList]
  );

  const jenisBebanMap = useMemo(
    () => new Map(jenisBebanList.map((item) => [String(item.id_jenis_beban || item.id || ''), item])),
    [jenisBebanList]
  );

  const detailHimpunMap = useMemo(
    () => new Map(detailHimpunList.map((item) => [String(item.id_detail_himpun || item.id || ''), item])),
    [detailHimpunList]
  );

  const himpunMap = useMemo(
    () => new Map(himpunList.map((item) => [String(item.id_himpun || item.id), item])),
    [himpunList]
  );

  const enrichedPengeluaranList = useMemo(() => {
    return scopedPengeluaranList.map((item) => {
      // Coba berbagai cara untuk mendapatkan detail_pengeluaran
      const detailId = String(resolveDetailPengeluaranId(item));
      let detail = item.detail_pengeluaran || item.detailPengeluaran || {};
      
      // Jika detail kosong, coba temukan berdasarkan ID
      if ((!detail || Object.keys(detail).length === 0) && detailId) {
        detail = detailPengeluaranMap.get(detailId) || {};
      }

      // Jika masih kosong, coba temukan detail_pengeluaran berdasarkan ID pengeluaran
      if ((!detail || Object.keys(detail).length === 0) && (item.id_pengeluaran || item.id)) {
        detail = detailPengeluaranByPengeluaranMap.get(String(item.id_pengeluaran || item.id)) || {};
      }
      
      // Selesaikan ID jenis beban dari berbagai sumber
      const jenisId = String(
        detail?.id_jenis_beban || 
        detail?.jenis_beban?.id_jenis_beban || 
        item?.id_jenis_beban || 
        item?.jenis_beban?.id_jenis_beban || 
        ''
      );
      const jenisBeban = detail?.jenis_beban || item?.jenis_beban || jenisBebanMap.get(jenisId) || {};

      // Selesaikan himpun dari berbagai sumber
      const himpun = item?.himpun || himpunMap.get(String(item?.id_himpun)) || {};
      
      // Selesaikan detail himpun dari berbagai sumber  
      const detailHimpun = item?.detail_himpun || item?.detailHimpun || detailHimpunMap.get(String(item?.id_detail_himpun)) || {};

      return {
        ...item,
        detailPengeluaran: detail,
        jenisBeban,
        himpun,
        detailHimpun,
      };
    });
  }, [detailHimpunMap, detailPengeluaranByPengeluaranMap, detailPengeluaranMap, himpunMap, jenisBebanMap, scopedPengeluaranList]);

  const filteredPengeluaranList = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return enrichedPengeluaranList;

    return enrichedPengeluaranList.filter((item) => [
      item.sumber_dana,
      resolveSourceDanaLabel(item.sumber_dana),
      item.user_input,
      item.lembaga,
      item.tanggal,
      item.departemen,
      item.detailPengeluaran?.keterangan,
      item.jenisBeban?.nama_beban,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(keyword)));
  }, [enrichedPengeluaranList, searchTerm]);

  const totalNominal = useMemo(() => (
    enrichedPengeluaranList.reduce((total, item) => total + (Number(item.detailPengeluaran?.nominal) || 0), 0)
  ), [enrichedPengeluaranList]);

  const saldoSumberDana = useMemo(() => {
    const grossBySource = new Map([
      ['zakat', 0],
      ['infak', 0],
      ['wakaf', 0],
    ]);

    scopedTransaksiList.forEach((item) => {
      if (!isSuccessfulDonation(item)) return;

      let sourceType = normalizeSourceDanaValue(
        resolveTransactionFundingType(item, detailDonasiList, resolveJenisDonasiName(item, jenisDonasiList))
      );
      if (!sourceType) {
        sourceType = normalizeSourceDanaValue(item.sumber_dana || item.sourceType || item.source_type || '');
      }
      if (!grossBySource.has(sourceType)) return;

      grossBySource.set(sourceType, grossBySource.get(sourceType) + resolveDonationAmount(item));
    });

    const distributedByCategory = calculateDistributedByCategory(scopedPenyaluranList, programList);
    const balances = new Map();

    ['zakat', 'infak', 'wakaf'].forEach((sourceType) => {
      const gross = grossBySource.get(sourceType) || 0;
      const hakAmil = calculateHakAmilForSource(sourceType, gross);
      const distributed = distributedByCategory.get(sourceType) || 0;
      balances.set(sourceType, Math.max(gross - hakAmil - distributed, 0));
    });

    const hakAmilZakat = calculateHakAmilForSource('zakat', grossBySource.get('zakat') || 0);
    const hakAmilInfak = calculateHakAmilForSource('infak', grossBySource.get('infak') || 0);
    const totalHakAmil = hakAmilZakat + hakAmilInfak;
    const totalDtPeduli = Math.round(hakAmilZakat * 0.60) + Math.round(hakAmilInfak * 0.60);

    balances.set('dana_amil', Math.max(totalHakAmil - totalDtPeduli, 0));

    // Hitung pengeluaran berdasarkan sumber dan kurangi dari saldo
    const pengeluaranBySource = new Map([
      ['zakat', 0],
      ['infak', 0],
      ['wakaf', 0],
      ['dana_amil', 0],
    ]);

    enrichedPengeluaranList.forEach((item) => {
      const normalizedSource = normalizeSourceDanaValue(item.sumber_dana);
      if (pengeluaranBySource.has(normalizedSource)) {
        const nominal = Number(item.detailPengeluaran?.nominal) || Number(item.nominal) || 0;
        pengeluaranBySource.set(normalizedSource, pengeluaranBySource.get(normalizedSource) + nominal);
      }
    });

    // Kurangi pengeluaran dari saldo
    pengeluaranBySource.forEach((amount, source) => {
      const currentBalance = balances.get(source) || 0;
      balances.set(source, Math.max(currentBalance - amount, 0));
    });

    return balances;
  }, [detailDonasiList, jenisDonasiList, scopedPenyaluranList, programList, scopedTransaksiList, enrichedPengeluaranList]);

  const selectedSaldoSumberDana = saldoSumberDana.get(normalizeSourceDanaValue(pengeluaranForm.sumber_dana)) || 0;
  const selectedHimpun = useMemo(() => (
    himpunList.find((item) => String(item.id_himpun || '') === String(pengeluaranForm.id_himpun || '')) || null
  ), [himpunList, pengeluaranForm.id_himpun]);

  const selectedHimpunLabel = selectedHimpun
    ? String(selectedHimpun.nama_himpun || selectedHimpun.kode_himpun || selectedHimpun.id_himpun || '')
    : 'Bank';

  const currentError =
    error ||
    createPengeluaranError ||
    updatePengeluaranError ||
    deletePengeluaranError ||
    createDetailError ||
    updateDetailError ||
    createJenisError ||
    updateJenisError ||
    deleteJenisError;

  const handlePengeluaranChange = (event) => {
    const { name, value } = event.target;
    setPengeluaranForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'id_himpun' ? { id_detail_himpun: '' } : {}),
    }));
  };

  const handleJenisChange = (event) => {
    const { name, value } = event.target;
    setJenisForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetPengeluaranForm = () => {
    setPengeluaranForm({
      ...initialPengeluaranForm,
      id_user: adminId,
      user_input: adminName,
      lembaga: lembagaScope.name,
    });
  };

  const resetJenisForm = () => {
    setJenisForm(initialJenisForm);
  };

  const refetchAll = async () => {
    await Promise.all([
      refetchPengeluaran(),
      refetchDetailPengeluaran(),
      refetchJenisBeban(),
    ]);
  };

  const handlePengeluaranSubmit = async (event) => {
    event.preventDefault();
    setActionMessage('');

    if (!pengeluaranForm.id_jenis_beban || !pengeluaranForm.nominal || !pengeluaranForm.tanggal) {
      setActionMessage('Jenis beban, nominal, dan tanggal wajib diisi.');
      return;
    }

    let detailId = pengeluaranForm.id_detail_pengeluaran;
    let pengeluaranId = pengeluaranForm.id_pengeluaran;
    let result = null;

    if (pengeluaranId) {
      const detailResult = pengeluaranForm.id_detail_pengeluaran
        ? await putDetailPengeluaran(
            pengeluaranForm.id_detail_pengeluaran,
            pengeluaranForm.id_jenis_beban,
            pengeluaranForm.nominal,
            pengeluaranForm.keterangan,
            pengeluaranForm.id_pengeluaran
          )
        : await postDetailPengeluaran(
            pengeluaranForm.id_jenis_beban,
            pengeluaranForm.nominal,
            pengeluaranForm.keterangan,
            pengeluaranForm.id_pengeluaran
          );

      if (!detailResult) return;

      detailId = pengeluaranForm.id_detail_pengeluaran || resolveId(detailResult, ['id_detail_pengeluaran', 'id']);

      result = await putPengeluaran(
        pengeluaranForm.id_pengeluaran,
        pengeluaranForm.id_user,
        detailId,
        pengeluaranForm.id_detail_himpun,
        pengeluaranForm.id_himpun,
        pengeluaranForm.sumber_dana,
        pengeluaranForm.user_input,
        pengeluaranForm.lembaga,
        pengeluaranForm.tanggal,
        pengeluaranForm.departemen
      );
    } else {
      result = await postPengeluaran(
        pengeluaranForm.id_user,
        null,
        pengeluaranForm.id_detail_himpun,
        pengeluaranForm.id_himpun,
        pengeluaranForm.sumber_dana,
        pengeluaranForm.user_input,
        pengeluaranForm.lembaga,
        pengeluaranForm.tanggal,
        pengeluaranForm.departemen
      );

      if (!result) return;

      pengeluaranId = resolveId(result, ['id_pengeluaran', 'id']);
      if (!pengeluaranId) return;

      const detailResult = await postDetailPengeluaran(
        pengeluaranForm.id_jenis_beban,
        pengeluaranForm.nominal,
        pengeluaranForm.keterangan,
        pengeluaranId
      );

      if (!detailResult) return;

      detailId = resolveId(detailResult, ['id_detail_pengeluaran', 'id']);
      if (!detailId) return;

      result = await putPengeluaran(
        pengeluaranId,
        pengeluaranForm.id_user,
        detailId,
        pengeluaranForm.id_detail_himpun,
        pengeluaranForm.id_himpun,
        pengeluaranForm.sumber_dana,
        pengeluaranForm.user_input,
        pengeluaranForm.lembaga,
        pengeluaranForm.tanggal,
        pengeluaranForm.departemen
      );
    }

    if (!result) return;

    setActionMessage(pengeluaranForm.id_pengeluaran ? 'Data pengeluaran berhasil diperbarui.' : 'Data pengeluaran berhasil ditambahkan.');
    resetPengeluaranForm();
    await refetchAll();
  };

  const handleEditPengeluaran = (item) => {
    const detail = item.detailPengeluaran || {};
    const detailId = resolveDetailPengeluaranId(item);

    setPengeluaranForm({
      id_pengeluaran: String(resolvePengeluaranId(item)),
      id_detail_pengeluaran: String(detailId),
      id_jenis_beban: String(detail.id_jenis_beban || item.id_jenis_beban || ''),
      nominal: String(detail.nominal || item.nominal || ''),
      keterangan: detail.keterangan || item.keterangan || '',
      id_user: String(item.id_user || adminId),
      id_detail_himpun: String(item.id_detail_himpun || ''),
      id_himpun: String(item.id_himpun || ''),
      sumber_dana: normalizeSourceDanaValue(item.sumber_dana),
      user_input: item.user_input || adminName,
      lembaga: item.lembaga || lembagaScope.name,
      tanggal: item.tanggal || new Date().toISOString().slice(0, 10),
      departemen: item.departemen || '',
    });
  };

  const handleDeletePengeluaran = (item) => {
    setDeleteTargetItem(item);
    setDeleteConfirmOpen(true);
  };

  const confirmDeletePengeluaran = async () => {
    if (!deleteTargetItem) return;

    if (!canDeletePengeluaran) {
      setActionMessage('Hubungi Super Admin atau Pimpinan untuk menghapus data pengeluaran ini.');
      setDeleteConfirmOpen(false);
      setDeleteTargetItem(null);
      return;
    }

    const id = resolvePengeluaranId(deleteTargetItem);
    if (!id) return;

    const deleted = await deletePengeluaran(id);
    if (deleted) {
      const detailId = resolveDetailPengeluaranId(deleteTargetItem);
      if (detailId) {
        await deleteDetailPengeluaran(detailId);
      }

      setActionMessage('Data pengeluaran berhasil dihapus.');
      await refetchAll();
    }

    setDeleteConfirmOpen(false);
    setDeleteTargetItem(null);
  };

  const cancelDeletePengeluaran = () => {
    setDeleteConfirmOpen(false);
    setDeleteTargetItem(null);
  };

  const handleJenisSubmit = async (event) => {
    event.preventDefault();
    setActionMessage('');

    if (!jenisForm.nama_beban.trim()) {
      setActionMessage('Nama beban wajib diisi.');
      return;
    }

    const result = jenisForm.id_jenis_beban
      ? await putJenisBeban(jenisForm.id_jenis_beban, jenisForm.nama_beban)
      : await postJenisBeban(jenisForm.nama_beban);

    if (result) {
      setActionMessage(jenisForm.id_jenis_beban ? 'Jenis beban berhasil diperbarui.' : 'Jenis beban berhasil ditambahkan.');
      resetJenisForm();
      await refetchJenisBeban();
    }
  };

  const handleEditJenis = (item) => {
    setJenisForm({
      id_jenis_beban: String(item.id_jenis_beban || ''),
      nama_beban: item.nama_beban || '',
    });
  };

  const handleDeleteJenis = async (id) => {
    const deleted = await deleteJenisBeban(id);
    if (deleted) {
      setActionMessage('Jenis beban berhasil dihapus.');
      await refetchJenisBeban();
    }
  };

  const isSavingPengeluaran = creatingPengeluaran || updatingPengeluaran || creatingDetail || updatingDetail;
  const isSavingJenis = creatingJenis || updatingJenis;
  const fieldClassName =
    'h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-500';
  const labelClassName = 'mb-2 block text-[10px] font-bold uppercase tracking-widest text-slate-500';
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-800">
      <div className="w-full space-y-7 px-4 py-8 sm:px-6 lg:px-10 xl:px-16">

        {/* ── HEADER ── */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />
          <div className="flex flex-col gap-5 px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/30 flex-shrink-0">
                <FiFileText className="text-white text-xl" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Operasional</p>
                <h1 className="mt-0.5 text-2xl font-bold leading-tight text-slate-900 lg:text-3xl">Pengeluaran</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Kelola pencatatan beban, sumber dana, dan saldo operasional lembaga.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={refetchAll}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              title="Segarkan data"
            >
              <FiRefreshCw className="h-4 w-4" />
              Segarkan
            </button>
          </div>
        </section>

        {/* ── METRIC CARDS ── */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="group relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-200 to-amber-100 p-5 shadow-md transition hover:shadow-xl hover:-translate-y-0.5 duration-200">
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300 rounded-2xl" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Total Data</p>
                <p className="mt-3 text-2xl font-extrabold text-black">{enrichedPengeluaranList.length}</p>
                <p className="mt-4 text-xs leading-5 text-blue-900">Jumlah seluruh catatan pengeluaran.</p>
              </div>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm flex-shrink-0">
                <FiFileText className="h-5 w-5" />
              </span>
            </div>
          </div>
          <div className="group relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-200 to-blue-100 p-5 shadow-md transition hover:shadow-xl hover:-translate-y-0.5 duration-200">
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300 rounded-2xl" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-900">Total Nominal</p>
                <p className="mt-3 text-2xl font-extrabold text-black">{formatCurrency(totalNominal)}</p>
                <p className="mt-4 text-xs leading-5 text-emerald-900">Akumulasi nominal seluruh pengeluaran.</p>
              </div>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-900 text-white shadow-sm flex-shrink-0">
                <FiDollarSign className="h-5 w-5" />
              </span>
            </div>
          </div>
        </div>

        {(actionMessage || currentError) && (
          <div className={`flex items-start gap-3 rounded-xl border p-4 ${
            currentError
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
              currentError ? 'bg-red-200' : 'bg-emerald-200'
            }`}>
              {currentError
                ? <FiXCircle className={`h-3 w-3 ${currentError ? 'text-red-700' : ''}`} />
                : <FiCheckCircle className="h-3 w-3 text-emerald-700" />}
            </div>
            <p className="text-sm font-medium">{currentError || actionMessage}</p>
          </div>
        )}

        <form onSubmit={handlePengeluaranSubmit} className="space-y-6">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-4 border-b border-slate-100 bg-gradient-to-r from-blue-100 to-blue-50 px-6 py-4">
              <div className="w-9 h-9 rounded-xl bg-blue-900 flex items-center justify-center flex-shrink-0">
                <FiFileText className="text-white h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Input Data</p>
                <h2 className="mt-0.5 text-base font-bold text-black">Form Pengeluaran</h2>
              </div>
            </div>

            <div className="space-y-6 p-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClassName}>Tanggal</label>
                  <input type="date" name="tanggal" value={pengeluaranForm.tanggal} onChange={handlePengeluaranChange} {...requiredFieldProps('Tanggal pengeluaran wajib diisi.')} className={fieldClassName} />
                </div>
                <div>
                  <label className={labelClassName}>Sumber Dana</label>
                  <select name="sumber_dana" value={pengeluaranForm.sumber_dana} onChange={handlePengeluaranChange} {...requiredFieldProps('Sumber dana wajib dipilih.')} className={fieldClassName}>
                    <option value="">Pilih sumber dana</option>
                    {SOURCE_DANA_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClassName}>Saldo Terakhir</label>
                  <input
                    value={pengeluaranForm.sumber_dana ? formatCurrency(selectedSaldoSumberDana) : 'Rp0'}
                    readOnly
                    className="h-11 w-full rounded-lg border border-blue-100 bg-blue-50 px-3 text-sm font-bold text-blue-900 shadow-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClassName}>Via Bayar</label>
                  <select name="id_himpun" value={pengeluaranForm.id_himpun} onChange={handlePengeluaranChange} {...requiredFieldProps('Via bayar wajib dipilih.')} className={fieldClassName}>
                    <option value="">Pilih bank</option>
                    {himpunList.filter((item) => {
                      const label = String(item.nama_himpun || item.kode_himpun || item.nama || item.kode || item.id_himpun || item.id || '').trim().toLowerCase();
                      return !label.includes('qris');
                    }).map((item) => (
                      <option key={item.id_himpun || item.id} value={item.id_himpun || item.id}>
                        {String(item.nama_himpun || item.kode_himpun || item.nama || item.kode || item.id_himpun || item.id || '').trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClassName}>Nomor Rekening</label>
                  <select
                    name="id_detail_himpun"
                    value={pengeluaranForm.id_detail_himpun}
                    onChange={handlePengeluaranChange}
                    disabled={!pengeluaranForm.id_himpun}
                    {...requiredFieldProps('Nomor rekening wajib dipilih.')}
                    className={fieldClassName}
                  >
                    <option value="">Pilih rekening</option>
                    {detailHimpunList.filter((item) => {
                      const selectedBank = himpunList.find((h) => String(h.id_himpun || h.id) === String(pengeluaranForm.id_himpun));
                      if (!selectedBank) return false;
                      const bankKode = selectedBank.kode_himpun || selectedBank.kode;
                      const itemKode = item.kode_himpun || item.kode;
                      return bankKode && itemKode && String(itemKode).toLowerCase() === String(bankKode).toLowerCase();
                    }).map((item) => (
                      <option key={item.id_detail_himpun || item.id} value={item.id_detail_himpun || item.id}>
                        {String(item.nama || item.nama_detail_himpun || item.kode_detail_himpun || item.id_detail_himpun || item.id || '').trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClassName}>Referensi</label>
                  <input name="user_input" value={pengeluaranForm.user_input} onChange={handlePengeluaranChange} placeholder="Nama referensi" {...requiredFieldProps('Referensi wajib diisi.')} className={fieldClassName} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClassName}>MPZ</label>
                  <input name="lembaga" value={pengeluaranForm.lembaga} onChange={handlePengeluaranChange} placeholder="Nama MPZ" {...requiredFieldProps('MPZ wajib diisi.')} className={fieldClassName} />
                </div>
                <div>
                  <label className={labelClassName}>Departemen</label>
                  <input name="departemen" value={pengeluaranForm.departemen} onChange={handlePengeluaranChange} placeholder="Nama departemen" {...requiredFieldProps('Departemen wajib diisi.')} className={fieldClassName} />
                </div>
                <div>
                  <label className={labelClassName}>ID User</label>
                  <input name="id_user" value={pengeluaranForm.id_user} onChange={handlePengeluaranChange} placeholder="ID User" {...requiredFieldProps('ID user wajib diisi.')} className={fieldClassName} />
                </div>
              </div>

              <div className="border-t border-blue-100 pt-6">
                <p className="mb-4 text-[10px] font-bold uppercase tracking-widest text-blue-900">Detail Beban</p>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className={labelClassName}>Jenis Transaksi *</label>
                    <select name="id_jenis_beban" value={pengeluaranForm.id_jenis_beban} onChange={handlePengeluaranChange} {...requiredFieldProps('Jenis transaksi wajib dipilih.')} className={fieldClassName}>
                      <option value="">Pilih jenis</option>
                      {jenisBebanList.map((item) => (
                        <option key={item.id_jenis_beban} value={item.id_jenis_beban}>{item.nama_beban}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClassName}>Nominal *</label>
                    <input type="number" min="0" name="nominal" value={pengeluaranForm.nominal} onChange={handlePengeluaranChange} placeholder="0" {...requiredFieldProps('Nominal wajib diisi.')} className={fieldClassName} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClassName}>Keterangan</label>
                    <input name="keterangan" value={pengeluaranForm.keterangan} onChange={handlePengeluaranChange} placeholder="Catatan pengeluaran" {...requiredFieldProps('Keterangan pengeluaran wajib diisi.')} className={fieldClassName} />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-blue-100 pt-5">
                <button
                  type="submit"
                  disabled={isSavingPengeluaran}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-900 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingPengeluaran ? 'Menyimpan...' : 'Simpan'}
                </button>
                <button
                  type="button"
                  onClick={resetPengeluaranForm}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <FiRotateCcw className="h-4 w-4" />
                  Bersihkan Form
                </button>
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-100 bg-gradient-to-r from-blue-100 to-blue-50 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Rekap Data</p>
                <h2 className="mt-0.5 text-base font-bold text-black">Daftar Pengeluaran</h2>
                <p className="mt-0.5 text-xs text-blue-900">{filteredPengeluaranList.length} data ditampilkan</p>
              </div>
              <div className="relative w-full lg:max-w-sm">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Cari jenis, MPZ, keterangan"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Jenis</th>
                    <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-widest text-slate-500">Nominal</th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Keterangan</th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Via Bayar</th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Tanggal</th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">MPZ</th>
                    <th className="px-5 py-3.5 text-right text-xs font-bold uppercase tracking-widest text-slate-500">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="relative mx-auto mb-1 w-12 h-12">
                            <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                            <div className="animate-spin absolute inset-0 rounded-full border-4 border-blue-900 border-t-transparent"></div>
                          </div>
                          <p className="text-slate-500 font-semibold">Memuat data pengeluaran...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredPengeluaranList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                            <FiFileText className="h-6 w-6 text-slate-300" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-700">Tidak ada data pengeluaran.</p>
                            <p className="mt-1 text-sm text-slate-400">Data akan tampil setelah pencatatan pengeluaran ditambahkan.</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredPengeluaranList.map((item) => {
                    const rowId = resolvePengeluaranId(item);
                    const himpunName = item.himpun?.nama_himpun || item.himpun?.kode_himpun || item.himpun?.id_himpun || item.id_himpun || '-';
                    const nominal = item.detailPengeluaran?.nominal || item.nominal || item.detailPengeluaran?.nomimal || 0;
                    const keterangan = item.detailPengeluaran?.keterangan || item.keterangan || item.detailPengeluaran?.ket || '-';

                    return (
                      <tr key={rowId} className="transition-colors duration-150 hover:bg-slate-50/80">
                        <td className="px-5 py-4 text-sm font-semibold text-slate-900">{item.jenisBeban?.nama_beban || '-'}</td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm font-bold text-emerald-700">{formatCurrency(nominal)}</span>
                        </td>
                        <td className="max-w-xs px-5 py-4 text-sm text-slate-600">
                          <span className="line-clamp-2">{keterangan}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">{himpunName}</td>
                        <td className="px-5 py-4 text-sm text-slate-500">{item.tanggal || '-'}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{item.lembaga || '-'}</td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditPengeluaran(item)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 transition hover:bg-blue-100 active:scale-95"
                              title="Edit pengeluaran"
                            >
                              <FiEdit2 className="h-3.5 w-3.5" />
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={deletingPengeluaran}
                              onClick={() => handleDeletePengeluaran(item)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition hover:bg-red-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                              title="Hapus pengeluaran"
                            >
                              <FiTrash2 className="h-3.5 w-3.5" />
                              Hapus
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </form>
      </div>

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.16)]">
            <div className="bg-slate-900 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-900">
                  <FiAlertTriangle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-amber-200">Konfirmasi</p>
                  <h2 className="mt-2 text-xl font-bold">Hapus data pengeluaran?</h2>
                </div>
              </div>
            </div>

            <div className="space-y-4 p-6">
              <p className="text-sm leading-6 text-slate-600">
                {canDeletePengeluaran
                  ? 'Data pengeluaran akan dihapus secara permanen. Pastikan informasi di bawah sudah benar sebelum melanjutkan.'
                  : 'Akun Anda tidak memiliki izin untuk menghapus data pengeluaran. Hubungi Super Admin atau Pimpinan untuk menghapus data ini.'}
              </p>

              {!canDeletePengeluaran ? (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  <p className="font-semibold">Akses dibatasi</p>
                  <p className="mt-2">Hanya Super Admin atau Pimpinan yang dapat menghapus data pengeluaran.</p>
                </div>
              ) : (
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">Ringkasan Pengeluaran</p>
                  <div className="mt-3 space-y-2">
                    <p>
                      <span className="font-semibold">Jenis:</span>{' '}
                      {deleteTargetItem?.jenisBeban?.nama_beban || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">Nominal:</span>{' '}
                      {formatCurrency(deleteTargetItem?.detailPengeluaran?.nominal || deleteTargetItem?.nominal || 0)}
                    </p>
                    <p>
                      <span className="font-semibold">Tanggal:</span>{' '}
                      {deleteTargetItem?.tanggal || '-'}
                    </p>
                    <p>
                      <span className="font-semibold">MPZ:</span>{' '}
                      {deleteTargetItem?.lembaga || '-'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={cancelDeletePengeluaran}
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
                >
                  {canDeletePengeluaran ? 'Batal' : 'Tutup'}
                </button>
                {canDeletePengeluaran && (
                  <button
                    type="button"
                    onClick={confirmDeletePengeluaran}
                    disabled={deletingPengeluaran}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {deletingPengeluaran ? 'Menghapus...' : 'Hapus Sekarang'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

export default PengeluaranAdmin;
